import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { Play, Search, Clock, Calendar, Youtube, X } from "lucide-react";
import { SOCIALS } from "@/data/site";
import EpisodeArtwork from "@/components/podcast/EpisodeArtwork";
import SubscribeOn from "@/components/podcast/SubscribeOn";
import StudioBackdrop from "@/components/podcast/StudioBackdrop";

export default function Episodes() {
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [activeTag, setActiveTag] = useState("all");
  const [openEp, setOpenEp] = useState(null);

  useEffect(() => {
    api
      .get("/episodes")
      .then((r) => {
        setEpisodes(r.data);
        if (window.location.hash) {
          const slug = window.location.hash.replace("#", "");
          const found = r.data.find((e) => e.slug === slug);
          if (found) setOpenEp(found);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const tags = useMemo(() => {
    const set = new Set();
    episodes.forEach((e) => e.tags?.forEach((t) => set.add(t)));
    return ["all", ...Array.from(set)];
  }, [episodes]);

  const filtered = useMemo(() => {
    return episodes.filter((e) => {
      const matchQ =
        !query ||
        e.title.toLowerCase().includes(query.toLowerCase()) ||
        e.guest.toLowerCase().includes(query.toLowerCase());
      const matchT = activeTag === "all" || e.tags?.includes(activeTag);
      return matchQ && matchT;
    });
  }, [episodes, query, activeTag]);

  return (
    <div data-testid="episodes-page" className="pt-32 pb-20 min-h-screen bg-[#0A0A0A] relative">
      <StudioBackdrop opacity={0.1} size={260} />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-12">
          <p className="text-xs tracking-[0.25em] uppercase font-bold text-[#E10600] mb-3">
            Full Archive
          </p>
          <h1 className="font-display text-6xl md:text-8xl uppercase tracking-tight leading-[0.9]">
            Episode <span className="text-[#E10600]">Library</span>
          </h1>
          <p className="mt-6 text-zinc-400 max-w-2xl">
            Every conversation, every guest, every drop. Catch the full catalog or
            subscribe on{" "}
            <a
              href={SOCIALS.youtube}
              target="_blank"
              rel="noreferrer"
              className="text-[#E10600] hover:underline inline-flex items-center gap-1"
            >
              YouTube <Youtube className="w-4 h-4" />
            </a>
            .
          </p>
        </div>

        {/* Controls */}
        <div className="mb-10 flex flex-col md:flex-row gap-6 md:items-center md:justify-between">
          <div className="relative max-w-md flex-1">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 pointer-events-none" />
            <input
              data-testid="episodes-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search episodes or guests…"
              className="input-edge pl-9"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {tags.map((t) => (
              <button
                key={t}
                data-testid={`tag-${t}`}
                onClick={() => setActiveTag(t)}
                className={`px-3 py-2 text-[10px] tracking-[0.2em] uppercase font-bold border transition-colors ${
                  activeTag === t
                    ? "border-[#E10600] bg-[#E10600] text-white"
                    : "border-[#262626] text-zinc-400 hover:text-white hover:border-zinc-500"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <p className="text-zinc-500 font-mono">Loading…</p>
        ) : episodes.length === 0 ? (
          <div
            data-testid="episodes-empty"
            className="border border-[#262626] p-10 md:p-16 text-center bg-[#0A0A0A]"
          >
            <p className="text-[10px] tracking-[0.3em] uppercase font-mono text-[#E10600] mb-4">
              Season 2 · Loading Soon
            </p>
            <h2 className="font-display text-4xl md:text-5xl uppercase leading-none">
              First Drops <span className="text-[#1E6FE0]">Incoming</span>
            </h2>
            <p className="mt-5 text-zinc-400 max-w-xl mx-auto">
              New episodes will land here Mondays at 7PM EST. Follow{" "}
              <a
                href={SOCIALS.youtube}
                target="_blank"
                rel="noreferrer"
                className="text-[#E10600] hover:underline inline-flex items-center gap-1"
              >
                @theproveyourpointpodcast <Youtube className="w-4 h-4" />
              </a>{" "}
              on YouTube so you don't miss the premiere.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#262626] border border-[#262626]">
            {filtered.map((ep, i) => (
              <button
                key={ep.slug}
                data-testid={`episode-item-${i}`}
                onClick={() => setOpenEp(ep)}
                className="text-left bg-[#0A0A0A] hover:bg-[#121212] group transition-colors"
              >
                <div className="relative">
                  <EpisodeArtwork episode={ep} />
                  <div className="absolute bottom-3 right-3 w-12 h-12 border border-white/50 bg-black/30 backdrop-blur-sm flex items-center justify-center group-hover:bg-[#E10600] group-hover:border-[#E10600] transition-colors">
                    <Play className="w-5 h-5 text-white" fill="currentColor" />
                  </div>
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-4 text-[10px] tracking-[0.2em] uppercase text-zinc-500 font-mono">
                    <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{ep.published_at}</span>
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{ep.duration}</span>
                  </div>
                  <h3 className="mt-2 font-display text-2xl uppercase leading-tight text-white group-hover:text-[#E10600] transition-colors">
                    {ep.title}
                  </h3>
                  <p className="mt-2 text-sm text-zinc-400">With {ep.guest}</p>
                </div>
              </button>
            ))}
          </div>
        )}

        {episodes.length > 0 && filtered.length === 0 && !loading && (
          <p className="text-zinc-500 font-mono mt-12 text-center">No episodes match your filter.</p>
        )}
      </div>

      <div className="mt-20">
        <SubscribeOn />
      </div>

      {openEp && (
        <div
          data-testid="episode-modal"
          className="fixed inset-0 z-[60] bg-black/90 backdrop-blur-md p-4 flex items-center justify-center"
          onClick={() => setOpenEp(null)}
        >
          <div
            className="bg-[#0A0A0A] border border-[#E10600] max-w-4xl w-full max-h-[90vh] overflow-y-auto relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              data-testid="episode-modal-close-x"
              onClick={() => setOpenEp(null)}
              className="absolute top-4 right-4 z-10 w-10 h-10 bg-black/70 border border-[#262626] hover:border-[#E10600] flex items-center justify-center"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="aspect-video w-full bg-black">
              <iframe
                title={openEp.title}
                src={`https://www.youtube.com/embed/${openEp.youtube_id}`}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
                data-testid="episode-player"
              />
            </div>
            <div className="p-6 md:p-8">
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#E10600]">{openEp.number}</p>
              <h2 className="mt-2 font-display text-4xl md:text-5xl uppercase leading-none">{openEp.title}</h2>
              <p className="mt-3 text-zinc-400">With {openEp.guest} · {openEp.duration} · {openEp.published_at}</p>
              <p className="mt-6 text-zinc-300">{openEp.description}</p>
              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href={SOCIALS.youtube}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="episode-modal-youtube"
                  className="btn-primary"
                >
                  Watch on YouTube
                  <Youtube className="w-4 h-4" />
                </a>
                <button
                  data-testid="episode-modal-close"
                  onClick={() => setOpenEp(null)}
                  className="btn-secondary"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
